import {
  Button,
  Divider,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import NameValueForm from "components/utils/NameValueForm";

const ActivityForm = ({ onSubmit, type = "create", initialValues }) => {
  const [activityForm, setActivityForm] = useState(initialValues);
  const [errors, setErrors] = useState({
    name: "",
    type: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    type: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(activityForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (activityForm.type === "list") {
      if (activityForm.values?.length <= 0) {
        toast.error(strings.values.isRequired);
        return;
      }
      if (activityForm.values?.some((val) => !val.name || !val.value)) {
        toast.error(strings.values.fieldsMissing);
        return;
      }
    }

    if (!hasErrors)
      onSubmit({
        ...activityForm,
      });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setActivityForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      type: "",
    });
    setActivityForm(initialValues);
  };

  const onChangeValues = (values) => {
    setActivityForm((prevSt) => ({
      ...prevSt,
      values: values ?? [],
    }));
  };

  useEffect(() => {
    setActivityForm(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={activityForm.name}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          ></TextField>
          <SelectField
            name="type"
            value={activityForm.type}
            label={<LabelRequired text={strings.fields.type.label} />}
            onChange={onChange}
            errorMessage={errors.type}
            hasError={!!errors.type}
          >
            <option value="">{globalStrings.selectOption}</option>
            {Object.entries(strings.fields.type.options).map((opt) => (
              <option value={opt[0]}>{opt[1]}</option>
            ))}
          </SelectField>
        </Grid>
        {activityForm.type === "list" && (
          <>
            <NameValueForm
              title={strings.values.title}
              values={activityForm.values}
              onChange={onChangeValues}
            />
            <Divider className="mt-2" />
          </>
        )}
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={activityForm.description}
          onChange={onChange}
        ></TextAreaField>
        <Grid
          gap="15px"
          templateColumns={{ base: "1fr", large: "2fr 2fr 2fr" }}
        >
          <TextField
            name="abbreviation"
            label={strings.fields.abbreviation.label}
            value={activityForm.abbreviation}
            onChange={onChange}
          ></TextField>
          <TextField
            name="unit"
            label={strings.fields.unit.label}
            value={activityForm.unit}
            onChange={onChange}
          ></TextField>
          <TextField
            name="defaultValue"
            label={strings.fields.defaultValue.label}
            value={activityForm.defaultValue}
            onChange={onChange}
          ></TextField>
        </Grid>
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
      },
      type: {
        label: "Tipo",
        options: {
          date: "Fecha",
          number: "Número",
          text: "Texto",
          list: "Lista",
          sign: "Firma",
          check: "Check"
        },
      },
      description: {
        label: "Descripción",
      },
      abbreviation: {
        label: "Abreviatura",
      },
      unit: {
        label: "Unidad",
      },
      defaultValue: {
        label: "Valor por defecto",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    values: {
      title: "Valores",
      fieldsMissing: "Faltan campos por rellenar en la sección de Valores",
      isRequired: "Debe existir al menos 1 valor",
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
  },
});

export default ActivityForm;
